import { Send, Trash2, TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";

import type { ClaimWithRelations } from "../../api/use-claim";
import {
  useClaimFormalNotice,
  useDeleteFormalNotice,
  useSendFormalNotice,
} from "../../api/use-claim-actions";
import { ClaimDocuments } from "../claim-documents";
import { CtaShell } from "./cta-shell";
import { GenerateFormalNotice } from "./generate-formal-notice";

/** Port of states-cta/FormalNoticeCTA.vue — UPCLAIM.WAIT_SENDS_MED. */
export function FormalNoticeCTA({ claim }: { claim: ClaimWithRelations }) {
  const { data: formalNotice, isPending } = useClaimFormalNotice(claim.id);
  const deleteFormalNotice = useDeleteFormalNotice(claim.id);
  const sendFormalNotice = useSendFormalNotice(claim.id);

  // The adversary set on the claim is the addressee; the effective airline's company is only the
  // fallback for claims that have none yet — same rule as the API when sending.
  const company = claim.adversaryCompany ?? claim.legalSpecification.effectiveAirline.legalData?.company;
  const postalAddress = company?.postalAddress;

  return (
    <CtaShell legalStepName={claim.claimLegalStep.name}>
      <p className="mt-4 text-sm leading-6 text-gray-500">
        <span className="leading-8 font-semibold text-gray-900">
          Préparer et envoyer la Mise en Demeure
          <br />
        </span>
        Bien vérifier les montants et Fiche entreprise (adresse postale de la compagnie, RCS, etc).
      </p>

      {!isPending ? (
        <div className="py-6">
          {/* Temporary code / To remove in a few weeks, once stuck claims are exhausted */}
          {formalNotice?.externalPartnerId ? (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm">
              <div className="flex gap-2">
                <TriangleAlert className="h-5 w-5 shrink-0 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-800">Une mise en demeure a déjà été envoyée</p>
                  <p className="mt-1 text-amber-700">
                    Supprimer les fichiers de preuve (accusé de réception, etc.) avant de renvoyer une
                    nouvelle mise en demeure.
                  </p>
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      onClick={() => deleteFormalNotice.mutate()}
                      disabled={deleteFormalNotice.isPending}
                    >
                      <Trash2 />
                      Supprimer les preuves
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {!formalNotice?.formalNoticePdfFileId ? (
            <div className="relative block py-4 text-sm">
              <GenerateFormalNotice claim={claim} formalNotice={formalNotice} />
              <div className="mt-4">
                <ClaimDocuments claim={claim} />
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm">
              <p className="font-medium text-blue-900">Expédier la Mise en Demeure</p>
              <div className="my-4 flex">
                {company?.lreEmailAddress ? (
                  <div className="leading-8 font-semibold text-gray-900">
                    Envoi par LRE à :
                    <div className="max-w-sm rounded-lg border border-gray-200 bg-white px-10 py-4 shadow-sm">
                      {company.lreEmailAddress}
                    </div>
                  </div>
                ) : (
                  <div className="leading-8 font-semibold text-gray-900">
                    Adresse de LRE non trouvée, envoi papier :
                    <div className="max-w-sm rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="space-y-1 text-sm font-normal">
                        {postalAddress?.nameLine ? (
                          <div className="font-semibold text-gray-900">{postalAddress.nameLine}</div>
                        ) : null}
                        {postalAddress?.streetAddress ? (
                          <div className="text-gray-700">{postalAddress.streetAddress}</div>
                        ) : null}
                        <div className="text-gray-700">{postalAddress?.city}</div>
                        {postalAddress?.zipCode ? (
                          <div className="text-gray-700">{postalAddress.zipCode}</div>
                        ) : null}
                        <div className="text-gray-700">
                          {postalAddress?.city}, {postalAddress?.zipCode} {postalAddress?.countryCode}
                        </div>
                        <div className="font-medium text-gray-700">{postalAddress?.countryCode}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4">
                <Button onClick={() => sendFormalNotice.mutate()} disabled={sendFormalNotice.isPending}>
                  <Send />
                  Envoyer maintenant
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </CtaShell>
  );
}
